import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "../cn";

type Tone = "warn" | "danger" | "info";

// Solid 1px token border (no alpha modifier — our colour vars hold full hsl(),
// so Tailwind's `/30` opacity syntax can't apply to them reliably).
const TONE: Record<Tone, string> = {
  warn: "border-warn bg-warnSoft text-warnStrong",
  danger: "border-bad bg-badSoft text-badStrong",
  info: "border-info bg-infoSoft text-infoStrong",
};

/**
 * A single prominent "work is waiting for you" banner. Render it only when there is
 * something to act on (callers guard on a count) — absence is the all-clear signal,
 * so there is deliberately no success/empty variant. One tone drives border, fill,
 * icon and text together. Used at the top of dashboards across every persona.
 */
export function AttentionBanner({
  tone = "warn",
  icon: Icon = AlertTriangle,
  title,
  meta,
  action,
  className,
}: {
  tone?: Tone;
  icon?: React.ElementType;
  title: React.ReactNode;
  /** Secondary detail, e.g. "oldest 32h". */
  meta?: React.ReactNode;
  /** The call to action (a button or link). */
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border px-4 py-3", TONE[tone], className)}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug">{title}</p>
        {meta && <p className="mt-0.5 text-xs text-mute">{meta}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** The standard pill-button used as an AttentionBanner action. */
export function AttentionAction({
  onClick,
  href,
  children,
  tone = "warn",
}: {
  onClick?: () => void;
  href?: string;
  children: React.ReactNode;
  tone?: Tone;
}) {
  const ring = tone === "danger" ? "border-bad text-badStrong" : tone === "info" ? "border-info text-infoStrong" : "border-warn text-warnStrong";
  const cls = cn(
    "focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border bg-card px-3 py-1.5 text-xs font-medium hover:bg-subtle",
    ring,
  );
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}
